import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { DateRange, FiltersState, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  ProjectManagementSpendingCostFilter,
  ProjectSpendingCostsDataCollectionDocument,
  ProjectSpendingCostsDataCollectionItemFragment,
  ProjectSpendingsCategoryOptionsFilterDocument,
  SubprojectOptionsFilterDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { getF1StatusColor } from 'modules/expenses/lib/expensables/statusF1Color'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import { InvoicesUploadModal } from 'modules/finance/components/InvoicesUploadModal'
import { invoiceStatusF1Color, mapStatusText } from 'modules/finance/lib/invoices'
import { IProjectSpendingPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SpendingPage/types'

interface ProjectSpendingCostsDataCollectionProps {
  projectId: number
  projectCurrency: string
  openSidePanel: (newData: IProjectSpendingPanelData) => void
  onCloseSidePanel?: () => void
  expensesEnabled: boolean
}

type GenericInFilter = {
  label: string
  type: 'in'
  options: {
    cache: boolean
    options: () => Promise<Array<{ label: string; value: number | string }>>
  }
}

const formatDateFilter = (dateOrRange?: Date | DateRange) => {
  if (!dateOrRange) return []
  if (dateOrRange instanceof Date) {
    return [dateOrRange.toISOString()]
  }
  const dates = [dateOrRange.from.toISOString()]
  if (dateOrRange.to) {
    dates.push(dateOrRange.to.toISOString())
  }
  return dates
}

const resolveFilters = (
  filters: FiltersState<{
    status: GenericInFilter
    subprojectIds: GenericInFilter
  }>
): ProjectManagementSpendingCostFilter | undefined => {
  const filter: ProjectManagementSpendingCostFilter = {}

  const status = filters?.status?.map(String).filter(Boolean) || []
  if (status.length) {
    filter.status = { in: status }
  }

  const subprojectIds = filters?.subprojectIds?.map(Number).filter(Boolean) || []
  if (subprojectIds.length) {
    filter.subprojectId = { in: subprojectIds }
  }

  return filter
}
const ProjectSpendingCostsDataCollection = ({
  projectId,
  projectCurrency,
  openSidePanel,
  onCloseSidePanel,
  expensesEnabled,
}: ProjectSpendingCostsDataCollectionProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const apolloClient = useApolloClient()

  const [showUploadInvoiceModal, setShowUploadInvoiceModal] = useState(false)
  const [triggerRefetch, setTriggerRefetch] = useState<number>(0)

  const handleUploadModalClose = () => {
    setShowUploadInvoiceModal(false)
    setTriggerRefetch(triggerRefetch + 1)
    onCloseSidePanel?.()
  }

  const itemOnClick = (item: ProjectSpendingCostsDataCollectionItemFragment) => () => {
    const expensable = item.expensable
    if (expensable) {
      openSidePanel({
        effectiveOn: item.effectiveOn,
        employeeName: expensable?.owner?.access.fullName || '',
        exchangeRate: item.exchangeRate,
        expensableId: expensable?.id,
        legalEntityLegalName: expensable?.owner?.legalEntity?.legalName,
        legalEntityAmountCents: Number(item.legalEntityAmountCents),
        legalEntityAmountCurrency: item.legalEntityAmountCurrency,
        originalAmountCents: item.originalAmountCents || 0,
        originalAmountCurrency: item.originalAmountCurrency || projectCurrency,
      })
    }

    const financialDocument = item.financialDocument
    if (financialDocument) {
      openSidePanel({
        effectiveOn: item.effectiveOn,
        employeeName: financialDocument?.vendor?.name || '',
        exchangeRate: item.exchangeRate,
        financialDocumentId: financialDocument.id,
        legalEntityLegalName: financialDocument?.vendor?.name || '',
        legalEntityAmountCents: Number(item.legalEntityAmountCents),
        legalEntityAmountCurrency: item.legalEntityAmountCurrency,
        originalAmountCents: item.originalAmountCents || 0,
        originalAmountCurrency: item.originalAmountCurrency || projectCurrency,
      })
    }
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: ProjectSpendingCostsDataCollectionDocument,
      mapData: (data) => data.projectManagement.spendingCostsConnection.nodes,
      mapVariables: ({ filters, sortings }) => ({
        projectId,
        sortOrder: sortings?.[0],
        filter: resolveFilters(filters),
        expenseCategories: filters?.expenseCategories?.map(String).filter(Boolean) || [],
        dateRange: filters?.effectiveOn ? formatDateFilter(filters.effectiveOn) : [],
      }),
      mapPagination: (data) => ({
        total: data.projectManagement.spendingCostsConnection.totalCount ?? 0,
      }),
      totalItemSummary: (totals) => {
        return i18n.tp('project_management.spending.total_items', { count: totals })
      },
      primaryActions: () => ({
        label: i18n.t('project_management.spending.table.actions.primary_action'),
        icon: Add,
        onClick: () => {
          track(trackingEvents.projectManagement.spending.clickOnCreatePurchaseInvoice)
          setShowUploadInvoiceModal(true)
        },
      }),
      itemOnClick,
      secondaryActions: {
        expanded: 1,
        actions: () => [
          {
            label: i18n.t('project_management.spending.table.actions.secondary_action'),
            icon: Add,
            enabled: expensesEnabled,
            onClick: () => {
              track(trackingEvents.projectManagement.spending.clickOnCreateExpense)
              navigate(navigation.myExpenses.list.fullPath())
            },
          },
        ],
      },
      filters: {
        status: {
          label: i18n.t('project_management.spending.table.headers.status'),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t('project_management.spending.table.filters.status.pending'),
                value: 'pending',
              },
              {
                label: i18n.t('project_management.spending.table.filters.status.approved'),
                value: 'approved',
              },
              {
                label: i18n.t('project_management.spending.table.filters.status.rejected'),
                value: 'rejected',
              },
            ],
          },
        },
        effectiveOn: {
          type: 'date',
          label: i18n.t('contract_versions.header.effective_on'),
          options: { mode: 'range', view: 'day' },
        },
        expenseCategories: {
          type: 'in',
          label: i18n.t('project_management.export.expense_category'),
          options: {
            cache: true,
            options: async () => {
              const result = await apolloClient.query({
                query: ProjectSpendingsCategoryOptionsFilterDocument,
              })
              return (
                result.data?.expenses.categoriesConnection.nodes.map((category) => ({
                  label: category.label,
                  value: category.id,
                })) || []
              )
            },
          },
        },
        subprojectIds: {
          type: 'in',
          label: i18n.t('project_management.imports.table.headers.subprojects'),
          options: {
            cache: true,
            options: async () => {
              const result = await apolloClient.query({
                query: SubprojectOptionsFilterDocument,
                fetchPolicy: 'cache-first',
                variables: { projectId },
              })
              return (
                result.data?.projectManagement.subprojectsConnection.nodes
                  .filter(
                    (subproject): subproject is { id: number; name: string } => !!subproject.id
                  )
                  .map((subproject) => ({
                    label: subproject.name,
                    value: subproject.id,
                  })) || []
              )
            },
          },
        },
      },

      sortings: {
        effectiveOn: {
          label: i18n.t('project_management.spending.table.headers.document_date'),
        },
      },
      defaultSortings: {
        field: 'effectiveOn',
        order: 'desc',
      },
    },
    [triggerRefetch]
  )

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('project_management.spending.table.headers.beneficiary'),
                  render: ({ expensable, financialDocument }) => {
                    const vendor = financialDocument?.vendor
                    const owner = expensable?.owner

                    if (owner) {
                      return {
                        type: 'person',
                        value: {
                          firstName: owner?.access?.firstName || '',
                          lastName: owner?.access?.lastName || '',
                          src: buildUrl(owner?.access?.avatar),
                        },
                      }
                    }

                    return {
                      type: 'text',
                      value: vendor?.name,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.spending.table.headers.amount'),
                  render: ({ legalEntityAmountCents, legalEntityAmountCurrency }) => {
                    return {
                      type: 'text',
                      value:
                        legalEntityAmountCents && isCurrency(legalEntityAmountCurrency ?? undefined)
                          ? MoneyWithCurrency({
                              amount: legalEntityAmountCents,
                              currency: legalEntityAmountCurrency,
                              optionalDecimals: true,
                              locale,
                            })
                          : undefined,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.spending.table.headers.status'),
                  render: ({ expensable, financialDocument }) => {
                    if (expensable) {
                      return {
                        type: 'dotTag',
                        value: {
                          label: getStatusLabel(expensable.status),
                          color: getF1StatusColor(expensable.status),
                        },
                      }
                    }

                    if (!financialDocument) return

                    return {
                      type: 'dotTag',
                      value: {
                        label: mapStatusText(financialDocument.status),
                        color: invoiceStatusF1Color(financialDocument.status),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('project_management.spending.table.headers.subproject'),
                  render: ({ subproject }) => {
                    return {
                      type: 'text',
                      value: subproject?.name,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.spending.table.headers.document_number'),
                  render: ({ financialDocument }) => {
                    return {
                      type: 'text',
                      value: financialDocument?.documentNumber,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.spending.table.headers.category'),
                  render: ({ financialDocument }) => {
                    const category =
                      financialDocument?.expensesFinancialDocumentAssociation?.expense?.category

                    if (!category) return

                    return {
                      type: 'tag',
                      value: {
                        label: category.name,
                      },
                    }
                  },
                },
                {
                  label: i18n.t('project_management.spending.table.headers.document_date'),
                  render: ({ effectiveOn }) => ({
                    type: 'date',
                    value: effectiveOn ? new Date(effectiveOn) : undefined,
                  }),
                  sorting: 'effectiveOn',
                },
              ],
            },
          },
        ]}
      />

      {showUploadInvoiceModal && (
        <InvoicesUploadModal onClose={handleUploadModalClose} projectId={projectId} />
      )}
    </>
  )
}

export default ProjectSpendingCostsDataCollection
